import { GridRowModes, GridToolbarContainer, GridActionsCellItem, useGridApiRef } from '@mui/x-data-grid-premium';
import { Field, FieldArray, useFormikContext } from 'formik';
import { v4 as uuidv4 } from 'uuid';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';

import { dataGridPremiumColumns, formatNumber } from '../../utils/helper';

import {
  DataGridIdentifiers,
  FormErrorUI,
  AutoCompleteSelectUI,
  AddButtonUi,
  DatagridPremiumUi,
  InputUI,
  CheckboxUI,
} from '../Interface';
import useConfig from '../../context/ConfigContext';
import { normalizeBankDefaults } from '../../utils/CompanyHelper';

function EditToolbar(props) {
  const { disabled, push, defaultBank } = props;
  const { config } = useConfig();
  const { values, validateField } = useFormikContext();

  const handleClick = async () => {
    const currencyCode = config.currencyCode ?? '';
    const hasBankInCurrency = values.companyBank.some((bank) => (bank.currencyCode ?? '') === currencyCode);
    const hasDefaultInCurrency = values.companyBank.some(
      (bank) => (bank.currencyCode ?? '') === currencyCode && bank.default
    );
    const newData = {
      ...defaultBank,
      id: uuidv4(),
      currencyCode,
      default: !hasBankInCurrency || !hasDefaultInCurrency,
    };

    await push(newData);

    const fieldsToValidate = [
      `companyBank.${values.companyBank.length ?? 0}.bankAccountNumber`,
      `companyBank.${values.companyBank.length ?? 0}.iban`,
      `companyBank.${values.companyBank.length ?? 0}.costCenterCode`,
      `companyBank.${values.companyBank.length ?? 0}.currencyCode`,
      `companyBank.${values.companyBank.length ?? 0}.description`,
    ];

    for (const field of fieldsToValidate) {
      await validateField(field);
    }
  };

  return (
    <GridToolbarContainer>
      <AddButtonUi disabled={disabled} onClick={handleClick} />
    </GridToolbarContainer>
  );
}

const CompanyBank = ({ disabled, defaultBank, autocompleteOptions }) => {
  const apiRef = useGridApiRef();
  const { config } = useConfig();
  const { values, validateField, setFieldValue, errors } = useFormikContext();

  const getValueIndex = (values, id) => {
    return values.companyBank.findIndex((rc) => rc.id === id);
  };

  const handleDefaultChange = (id, checked, dataApi) => {
    const currentRow = values.companyBank.find((bank) => bank.id === id);
    if (!currentRow) return;

    const sameCurrencyRows = values.companyBank.filter(
      (bank) => bank.id !== id && (bank.currencyCode ?? '') === (currentRow.currencyCode ?? '')
    );
    const preferredDefaultId = checked ? id : sameCurrencyRows[0]?.id ?? id;
    const normalizedRows = normalizeBankDefaults(values.companyBank, preferredDefaultId);

    setFieldValue('companyBank', normalizedRows);
    normalizedRows.forEach((bank) => {
      dataApi.setEditCellValue({ id: bank.id, field: 'default', value: bank.default });
    });
  };

  const handleCurrencyChange = (id, currencyCode, dataApi) => {
    const currentIndex = getValueIndex(values, id);
    if (currentIndex < 0) return;

    const rowsWithChangedCurrency = values.companyBank.map((bank, index) =>
      index === currentIndex ? { ...bank, currencyCode } : bank
    );
    const existingDefaultInNewCurrency = rowsWithChangedCurrency.find(
      (bank) => bank.id !== id && (bank.currencyCode ?? '') === (currencyCode ?? '') && bank.default
    );
    const preferredDefaultId =
      existingDefaultInNewCurrency?.id ?? (rowsWithChangedCurrency[currentIndex].default ? id : null);
    const normalizedRows = normalizeBankDefaults(rowsWithChangedCurrency, preferredDefaultId);

    setFieldValue('companyBank', normalizedRows);
    dataApi.setEditCellValue({ id, field: 'currencyCode', value: currencyCode });
    normalizedRows.forEach((bank) => {
      dataApi.setEditCellValue({ id: bank.id, field: 'default', value: bank.default });
    });
  };

  const handleRemove = (id) => {
    const remainingRows = values.companyBank.filter((bank) => bank.id !== id);
    setFieldValue('companyBank', normalizeBankDefaults(remainingRows));
  };

  const formatBankAccountNumber = (value, row) => {
    if (!config?.companyBankAccountAutoFormat || !row.isNew || row.currencyCode !== config.currencyCode) {
      return value;
    }

    return formatNumber(value, [8, 8, 8]);
  };

  const columns = (setFieldValue, validateField, errors) => {
    return dataGridPremiumColumns([
      {
        field: 'bankAccountNumber',
        headerName: 'Bankszámlaszám',
        type: 'string',
        editable: true,
        renderEditCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);

          return (
            <Field
              name={`companyBank.${valueIndex}.bankAccountNumber`}
              type="text"
              variant="standard"
              as={InputUI}
              fullWidth
              helperText={<FormErrorUI message={errors?.companyBank?.[valueIndex]?.bankAccountNumber} />}
              onChange={(e) => {
                const formattedValue = formatBankAccountNumber(e.target.value, values.companyBank[valueIndex]);

                setFieldValue(`companyBank.${valueIndex}.bankAccountNumber`, formattedValue);

                data.api.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: formattedValue,
                });
              }}
              onBlur={async () => await validateField(`companyBank.${valueIndex}.bankAccountNumber`)}
            />
          );
        },
      },
      {
        field: 'iban',
        headerName: 'IBAN szám',
        type: 'string',
        editable: true,
        renderEditCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);

          return (
            <Field
              name={`companyBank.${valueIndex}.iban`}
              type="text"
              variant="standard"
              as={InputUI}
              fullWidth
              helperText={<FormErrorUI message={errors?.companyBank?.[valueIndex]?.iban} />}
              onChange={(e) => {
                setFieldValue(`companyBank.${valueIndex}.iban`, e.target.value ?? '');
                data.api.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: e.target.value,
                });
              }}
              onBlur={async () => await validateField(`companyBank.${valueIndex}.iban`)}
            />
          );
        },
      },
      {
        field: 'costCenterCode',
        headerName: 'Költséghely',
        type: 'string',
        editable: true,
        renderCell: (data) => {
          const findedItem = autocompleteOptions?.costCenter.find((item) => item.value === data.row?.costCenterCode);
          return findedItem?.label;
        },
        renderEditCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);
          const findedItem = autocompleteOptions.costCenter.find((item) => item.value === data.row.costCenterCode);

          return (
            <Field
              name={`companyBank.${valueIndex}.costCenterCode`}
              component={AutoCompleteSelectUI}
              helperText={<FormErrorUI message={errors?.companyBank?.[valueIndex]?.costCenterCode} />}
              selectedValue={values.companyBank?.[valueIndex]?.costCenterCode}
              selectedLabelValue={findedItem?.label}
              onChange={(_e, newVal) => {
                setFieldValue(`companyBank.${valueIndex}.costCenterCode`, newVal?.value ?? null);
                data.api.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: newVal.value ?? null,
                });
              }}
              dataset={autocompleteOptions.costCenter}
              onBlur={async () => await validateField(`companyBank.${valueIndex}.costCenterCode`)}
            />
          );
        },
      },
      {
        field: 'currencyCode',
        headerName: 'Pénznem',
        type: 'string',
        editable: true,
        renderCell: (data) => {
          const findedItem = autocompleteOptions?.currency.find((item) => item.value === data.row?.currencyCode);
          return findedItem?.label;
        },
        renderEditCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);
          const findedItem = autocompleteOptions.currency.find((item) => item.value === data.row.currencyCode);

          return (
            <Field
              name={`companyBank.${valueIndex}.currencyCode`}
              component={AutoCompleteSelectUI}
              helperText={<FormErrorUI message={errors?.companyBank?.[valueIndex]?.currencyCode} />}
              selectedValue={values.companyBank?.[valueIndex]?.currencyCode}
              selectedLabelValue={findedItem?.label}
              onChange={(_e, newVal) => {
                handleCurrencyChange(data.id, newVal?.value ?? null, data.api);
              }}
              dataset={autocompleteOptions.currency}
              onBlur={async () => await validateField(`companyBank.${valueIndex}.currencyCode`)}
            />
          );
        },
      },
      {
        field: 'default',
        headerName: 'Alapértelmezett',
        type: 'boolean',
        editable: true,
        renderEditCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);

          return (
            <Field
              name={`companyBank.${valueIndex}.default`}
              component={CheckboxUI}
              helperText={<FormErrorUI message={errors?.companyBank?.[valueIndex]?.default} />}
              checked={values.companyBank?.[valueIndex]?.default}
              variant="standard"
              onChange={(e) => handleDefaultChange(data.id, e.target.checked, data.api)}
              onBlur={async () => await validateField(`companyBank.${valueIndex}.default`)}
            />
          );
        },
      },
      {
        field: 'description',
        headerName: 'Megjegyzés',
        type: 'string',
        editable: true,
        renderEditCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);

          return (
            <Field
              name={`companyBank.${valueIndex}.description`}
              type="text"
              variant="standard"
              as={InputUI}
              fullWidth
              helperText={<FormErrorUI message={errors?.companyBank?.[valueIndex]?.description} />}
              onChange={(e) => {
                setFieldValue(`companyBank.${valueIndex}.description`, e.target.value ?? '');
                data.api.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: e.target.value,
                });
              }}
              onBlur={async () => await validateField(`companyBank.${valueIndex}.description`)}
            />
          );
        },
      },
      {
        field: 'actions',
        type: 'actions',
        headerName: 'Szerk.',
        cellClassName: 'actions',
        sortable: false,
        getActions: ({ id }) => {
          return [
            <GridActionsCellItem
              icon={<DeleteIcon />}
              label="Delete"
              onClick={() => {
                handleRemove(id);
              }}
              color="inherit"
              disabled={disabled}
            />,
          ];
        },
      },
    ]);
  };

  return (
    <div className="overflow-x-auto">
      <div className="align-middle">
        <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
          <FieldArray name="companyBank">
            {({ push }) => (
              <DatagridPremiumUi
                apiRef={apiRef}
                datagridIdentifier={DataGridIdentifiers.CompanyBank}
                height={400}
                columns={columns(setFieldValue, validateField, errors)}
                rows={values?.companyBank ?? []}
                slots={{
                  toolbar: EditToolbar,
                }}
                headerFilters={false}
                slotProps={{
                  toolbar: {
                    disabled,
                    push,
                    defaultBank,
                  },
                }}
                onCellDoubleClick={(_params, event) => {
                  event.defaultMuiPrevented = true;
                }}
                editMode="row"
                getRowHeight={() => 'auto'}
                onCellClick={(params, event) => {
                  if (disabled) return;
                  const rowMode = apiRef.current.getRowMode(params.row.id);
                  if (rowMode === GridRowModes.View) apiRef.current.startRowEditMode({ id: params.row.id });
                }}
              />
            )}
          </FieldArray>
        </div>
      </div>
    </div>
  );
};

export default CompanyBank;
